import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Breadcrumb,
  Collapse,
  Flex,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  TreeSelect,
  Typography,
  theme,
} from 'antd'
import type { FormInstance, TreeSelectProps } from 'antd'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { shortId } from '@/shared/lib/shortId'
import { withCurrentOption } from '@/shared/ui/currentOption'
import { actorDisplayName } from '@/features/identity/actorName'
import { fetchAllUsers } from '@/features/identity/identityApi'
import {
  ancestorChain,
  buildOrgTree,
  createOrgNode,
  orgTypeName,
  updateOrgNode,
  type OrgNode,
  type OrgNodeType,
  type OrgTreeNode,
} from './orgApi'
import {
  TOP_LEVEL,
  canHoldChildren,
  describeType,
  headOfficeTypes,
  isOfficeNode,
  isOfficeType,
  suggestCode,
  typesForExisting,
  typesUnder,
} from './orgSetup'

const CODE_PATTERN = /^[a-zA-Z0-9._-]{2,64}$/

type LocationOption = NonNullable<TreeSelectProps['treeData']>[number]

interface OrgNodeFormModalProps {
  open: boolean
  node: OrgNode | null
  location: string | null
  presetKind: string | null
  nodes: readonly OrgNode[]
  types: readonly OrgNodeType[]
  onClose: () => void
}

interface FormValues {
  kind: string
  displayName: string
  location?: string
  managerUserId?: string
  code: string
}

export function OrgNodeFormModal({
  open,
  node,
  location,
  presetKind,
  nodes,
  types,
  onClose,
}: OrgNodeFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canPickManager = can('user.read')
  const chosenLocation = Form.useWatch('location', form)

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const managerUserId = canPickManager
        ? (values.managerUserId ?? null)
        : (node?.managerUserId ?? null)
      const draft = {
        code: values.code.trim(),
        displayName: values.displayName.trim(),
        kind: values.kind,
        managerUserId,
      }
      if (node !== null) return updateOrgNode(node.id, { ...draft, parentId: node.parentId })
      const parentId = values.location === undefined || values.location === TOP_LEVEL ? null : values.location
      return createOrgNode({ ...draft, parentId })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.org.all })
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  const title =
    node !== null
      ? t('org.node.editTitle', { name: node.displayName })
      : chosenLocation === TOP_LEVEL
        ? t('org.createHeadOffice')
        : t('org.node.createTitle')

  return (
    <Modal
      open={open}
      title={title}
      onCancel={close}
      onOk={() => form.submit()}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={620}
    >
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <OrgNodeFields
        form={form}
        node={node}
        location={location}
        presetKind={presetKind}
        nodes={nodes}
        types={types}
        canPickManager={canPickManager}
        disabled={save.isPending}
        onSubmit={(values) => save.mutate(values)}
      />
    </Modal>
  )
}

interface OrgNodeFieldsProps {
  form: FormInstance<FormValues>
  node: OrgNode | null
  location: string | null
  presetKind: string | null
  nodes: readonly OrgNode[]
  types: readonly OrgNodeType[]
  canPickManager: boolean
  disabled: boolean
  onSubmit: (values: FormValues) => void
}

function OrgNodeFields({
  form,
  node,
  location,
  presetKind,
  nodes,
  types,
  canPickManager,
  disabled,
  onSubmit,
}: OrgNodeFieldsProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const isEdit = node !== null
  const [codeEdited, setCodeEdited] = useState(isEdit)
  const [moreOpen, setMoreOpen] = useState(false)

  const kind = Form.useWatch('kind', form)
  const displayName = Form.useWatch('displayName', form)
  const chosenLocation = Form.useWatch('location', form)

  const users = useQuery({
    queryKey: queryKeys.identity.users,
    queryFn: fetchAllUsers,
    enabled: canPickManager,
    staleTime: 5 * 60 * 1000,
  })
  const managerOptions = withCurrentOption(
    (users.data ?? []).map((user) => ({ value: user.id, label: actorDisplayName(user) })),
    node?.managerUserId ?? null,
    (value) => ({ value, label: shortId(value) }),
  )

  const locationTree = useMemo(() => buildLocationTree(nodes, types), [nodes, types])
  const locationOptions = useMemo<LocationOption[]>(
    () =>
      headOfficeTypes(types).length > 0
        ? [{ value: TOP_LEVEL, title: t('org.node.topLevelOption') }, ...locationTree]
        : locationTree,
    [locationTree, types, t],
  )

  const typeOptions = useMemo(() => {
    if (node !== null) return typesForExisting(types, nodes, node)
    if (chosenLocation === undefined) return types.filter(isOfficeType)
    const parent = chosenLocation === TOP_LEVEL ? null : (nodes.find((item) => item.id === chosenLocation) ?? null)
    return typesUnder(types, parent)
  }, [node, types, nodes, chosenLocation])

  const existingCodes = useMemo(
    () => nodes.filter((item) => item.id !== node?.id).map((item) => item.code),
    [nodes, node],
  )

  useEffect(() => {
    if (isEdit || chosenLocation === undefined) return
    const current = form.getFieldValue('kind') as string | undefined
    if (current !== undefined && typeOptions.some((type) => type.code === current)) return
    form.setFieldValue('kind', typeOptions.length === 1 ? typeOptions[0]?.code : undefined)
  }, [isEdit, chosenLocation, typeOptions, form])

  useEffect(() => {
    if (codeEdited) return
    form.setFieldValue('code', suggestCode(displayName ?? '', kind ?? null, existingCodes))
  }, [codeEdited, displayName, kind, existingCodes, form])

  const initialValues: Partial<FormValues> = node
    ? {
        kind: node.kind,
        displayName: node.displayName,
        managerUserId: node.managerUserId ?? undefined,
        code: node.code,
      }
    : {
        kind: presetKind ?? undefined,
        displayName: '',
        location: location ?? (locationTree.length === 0 ? TOP_LEVEL : undefined),
        code: '',
      }

  const topLevelOnly = !isEdit && locationTree.length === 0
  const creatingHeadOffice = chosenLocation === TOP_LEVEL
  const offersAgents = !isEdit && types.some((type) => type.settlementParty)
  const parentChain = node === null ? [] : ancestorChain(nodes, node.parentId)

  return (
    <Form<FormValues>
      form={form}
      layout="vertical"
      preserve={false}
      disabled={disabled}
      initialValues={initialValues}
      onFinish={onSubmit}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.some((field) => field.name[0] === 'code')) setMoreOpen(true)
      }}
      onValuesChange={(changed: Partial<FormValues>) => {
        if ('code' in changed) setCodeEdited(true)
      }}
    >
      <Form.Item
        name="kind"
        label={t('org.node.type')}
        rules={[{ required: true, message: t('org.node.validation.typeRequired') }]}
        extra={
          offersAgents ? (
            <span>
              {t('org.agentsNote')}{' '}
              {can('agent.read') ? <Link to="/agents">{t('nav.agents')}</Link> : null}
            </span>
          ) : undefined
        }
      >
        <TypeCards options={typeOptions} locked={isEdit && typeOptions.length === 1} />
      </Form.Item>

      <Form.Item
        name="displayName"
        label={t('org.node.displayName')}
        rules={[
          { required: true, whitespace: true, message: t('org.node.validation.nameRequired') },
          { max: 160, message: t('org.node.validation.nameLength') },
        ]}
      >
        <Input
          autoFocus
          placeholder={
            creatingHeadOffice ? t('org.node.headOfficePlaceholder') : t('org.node.namePlaceholder')
          }
        />
      </Form.Item>

      {isEdit ? (
        <Form.Item label={t('org.node.location')} extra={t('org.node.locationLocked')}>
          {parentChain.length === 0 ? (
            <Typography.Text>{t('org.node.topLevel')}</Typography.Text>
          ) : (
            <Breadcrumb items={parentChain.map((item) => ({ title: item.displayName }))} />
          )}
        </Form.Item>
      ) : (
        <Form.Item
          name="location"
          label={t('org.node.location')}
          hidden={topLevelOnly}
          rules={[{ required: true, message: t('org.node.validation.locationRequired') }]}
        >
          <TreeSelect
            treeData={locationOptions}
            treeDefaultExpandAll
            showSearch
            treeNodeFilterProp="title"
            placeholder={t('org.node.locationPlaceholder')}
          />
        </Form.Item>
      )}

      <Form.Item
        name="managerUserId"
        label={t('org.node.managerOptional')}
        extra={canPickManager ? t('org.node.managerHint') : t('org.node.managerNoDirectory')}
      >
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          disabled={!canPickManager}
          loading={users.isPending && canPickManager}
          placeholder={t('org.node.managerPlaceholder')}
          options={managerOptions}
        />
      </Form.Item>

      <Collapse
        ghost
        size="small"
        activeKey={moreOpen ? ['more'] : []}
        onChange={(keys) => setMoreOpen(keys.length > 0)}
        items={[
          {
            key: 'more',
            label: t('org.node.moreSettings'),
            forceRender: true,
            children: (
              <Form.Item
                name="code"
                label={t('org.node.code')}
                extra={t('org.node.codeHint')}
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: t('org.node.validation.codeRequired') },
                  { pattern: CODE_PATTERN, message: t('org.node.validation.codeFormat') },
                  {
                    validator: (_, value: string | undefined) =>
                      value !== undefined &&
                      existingCodes.some((code) => code.toLowerCase() === value.trim().toLowerCase())
                        ? Promise.reject(new Error(t('org.node.validation.codeTaken')))
                        : Promise.resolve(),
                  },
                ]}
              >
                <Input dir="ltr" />
              </Form.Item>
            ),
          },
        ]}
      />
    </Form>
  )
}

interface TypeCardsProps {
  value?: string
  onChange?: (value: string) => void
  options: readonly OrgNodeType[]
  locked: boolean
}

function TypeCards({ value, onChange, options, locked }: TypeCardsProps) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  if (options.length === 0) {
    return <Typography.Text type="secondary">{t('org.node.noTypesHere')}</Typography.Text>
  }

  return (
    <Radio.Group
      value={value}
      disabled={locked}
      onChange={(event) => onChange?.(String(event.target.value))}
      style={{ width: '100%' }}
    >
      <Flex vertical gap={8}>
        {options.map((type) => {
          const active = value === type.code
          return (
            <Radio
              key={type.code}
              value={type.code}
              style={{
                margin: 0,
                padding: '10px 12px',
                width: '100%',
                border: `1px solid ${active ? token.colorPrimary : token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                background: active ? token.colorPrimaryBg : token.colorBgContainer,
              }}
            >
              <Typography.Text strong>{orgTypeName(type, t)}</Typography.Text>
              <br />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {describeType(type, t)}
              </Typography.Text>
            </Radio>
          )
        })}
      </Flex>
    </Radio.Group>
  )
}

function buildLocationTree(
  nodes: readonly OrgNode[],
  types: readonly OrgNodeType[],
): LocationOption[] {
  const convert = (item: OrgTreeNode): LocationOption | null => {
    if (item.state === 'CLOSED' || !isOfficeNode(item, types)) return null
    const children = item.children
      .map(convert)
      .filter((child): child is LocationOption => child !== null)
    const selectable = canHoldChildren(item, types)
    if (!selectable && children.length === 0) return null
    return { value: item.id, title: item.displayName, disabled: !selectable, children }
  }

  return buildOrgTree(nodes)
    .map(convert)
    .filter((option): option is LocationOption => option !== null)
}
